/**
 * Support Thread – enterprise redesign with timeline and internal notes
 */
import { useEffect, useRef } from 'react';
import { cn } from '@respectly/ui';
import { User, Shield, Lock, Paperclip, FileText, Image, File } from 'lucide-react';
import type { SupportRequest, SupportMessage } from '@respectly/shared-types';
import { SupportStatusBadge } from './SupportStatusBadge';
import { SupportPriorityBadge } from './SupportPriorityBadge';
import { format } from 'date-fns';

interface SupportThreadProps {
  request: SupportRequest;
  messages: SupportMessage[];
  isLoading?: boolean;
  className?: string;
}

function getFileIcon(fileName: string) {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return { Icon: Image, color: 'text-green-500' };
  if (['pdf'].includes(ext)) return { Icon: FileText, color: 'text-red-500' };
  if (['doc', 'docx'].includes(ext)) return { Icon: FileText, color: 'text-blue-500' };
  return { Icon: File, color: 'text-zinc-400' };
}

export function SupportThread({ request, messages, isLoading, className }: SupportThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Request header */}
      <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800 px-5 py-4 bg-white dark:bg-zinc-900">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-[10px] text-zinc-400 dark:text-zinc-500 tracking-wide bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded ring-1 ring-zinc-200 dark:ring-zinc-700">
                #{request.id.slice(0, 8).toUpperCase()}
              </span>
              {request.tenant && (
                <span className="text-xs text-zinc-400 dark:text-zinc-500 truncate">
                  {request.tenant.name}
                </span>
              )}
            </div>
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 leading-snug">
              {request.subject}
            </h2>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <SupportPriorityBadge priority={request.priority} size="sm" />
            <SupportStatusBadge status={request.status} size="sm" />
          </div>
        </div>

        {/* Description */}
        <div className="rounded-xl bg-zinc-50 dark:bg-zinc-800/50 ring-1 ring-zinc-200 dark:ring-zinc-700 p-3.5 border-l-2 border-l-zinc-300 dark:border-l-zinc-600">
          <p className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider mb-1.5">
            Original Request
          </p>
          <p className="text-xs text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed">
            {request.description}
          </p>
          <p className="text-[11px] text-zinc-400 mt-2">
            Submitted {format(new Date(request.createdAt), "MMM d, yyyy 'at' h:mm a")}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-5">
        {isLoading ? (
          <div className="space-y-5">
            <MessageSkeleton />
            <MessageSkeleton isAdmin />
            <MessageSkeleton />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-10 w-10 rounded-xl bg-zinc-100 dark:bg-zinc-800 ring-1 ring-zinc-200 dark:ring-zinc-700 flex items-center justify-center mb-3">
              <User className="h-5 w-5 text-zinc-400" />
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              No messages yet. Start the conversation below.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-3.5 top-0 bottom-0 w-px bg-zinc-100 dark:bg-zinc-800 pointer-events-none" />
            <div className="space-y-5">
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
            </div>
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Message Bubble ─────────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: SupportMessage }) {
  const isSuperAdmin = message.authorType === 'super_admin';
  const isInternal = message.isInternal;

  return (
    <div className={cn('flex gap-3', isSuperAdmin ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar */}
      <div
        className={cn(
          'shrink-0 h-7 w-7 rounded-full flex items-center justify-center ring-1 z-10',
          isInternal
            ? 'bg-amber-100 dark:bg-amber-900/40 ring-amber-300 dark:ring-amber-700'
            : isSuperAdmin
            ? 'bg-blue-500 ring-blue-400/40'
            : 'bg-white dark:bg-zinc-800 ring-zinc-200 dark:ring-zinc-700'
        )}
      >
        {isInternal ? (
          <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
        ) : isSuperAdmin ? (
          <Shield className="h-3.5 w-3.5 text-white" />
        ) : (
          <User className="h-3.5 w-3.5 text-zinc-500 dark:text-zinc-400" />
        )}
      </div>

      {/* Content */}
      <div className={cn('max-w-[78%] space-y-1', isSuperAdmin ? 'items-end flex flex-col' : '')}>
        {/* Author + timestamp */}
        <div className={cn('flex items-center gap-2', isSuperAdmin ? 'flex-row-reverse' : '')}>
          <span className="text-[11px] font-semibold text-zinc-600 dark:text-zinc-300">
            {message.authorName ?? (isSuperAdmin ? 'Support Admin' : 'Customer')}
          </span>
          <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
            {format(new Date(message.createdAt), 'MMM d, h:mm a')}
          </span>
          {isInternal && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded ring-1 ring-amber-200 dark:ring-amber-800">
              <Lock className="h-2.5 w-2.5" />
              Internal
            </span>
          )}
        </div>

        {/* Bubble */}
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2.5',
            isInternal
              ? 'bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-200 dark:ring-amber-800 rounded-tr-sm'
              : isSuperAdmin
              ? 'bg-blue-600 text-white rounded-tr-sm shadow-md shadow-blue-600/15'
              : 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 ring-1 ring-zinc-200 dark:ring-zinc-700 shadow-sm rounded-tl-sm'
          )}
        >
          <p
            className={cn(
              'text-sm whitespace-pre-wrap leading-relaxed',
              isInternal ? 'text-amber-900 dark:text-amber-100' :
              isSuperAdmin ? 'text-white' : 'text-zinc-900 dark:text-zinc-100'
            )}
          >
            {message.content}
          </p>

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className={cn(
              'mt-2.5 space-y-1 border-t pt-2',
              isSuperAdmin && !isInternal ? 'border-white/20' : 'border-zinc-200 dark:border-zinc-700'
            )}>
              {message.attachments.map((att, idx) => {
                const { Icon, color } = getFileIcon(att.fileName);
                return (
                  <a
                    key={idx}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'flex items-center gap-1.5 text-xs rounded-lg px-2 py-1 transition-colors',
                      isSuperAdmin && !isInternal
                        ? 'text-blue-100 hover:bg-white/10'
                        : 'text-blue-500 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                    )}
                  >
                    <Icon className={cn('h-3 w-3 shrink-0', isSuperAdmin && !isInternal ? 'text-blue-100' : color)} />
                    <span className="truncate max-w-[200px]">{att.fileName}</span>
                    <Paperclip className="h-2.5 w-2.5 ml-auto opacity-60" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function MessageSkeleton({ isAdmin = false }: { isAdmin?: boolean }) {
  return (
    <div className={cn('flex gap-3 animate-pulse', isAdmin ? 'flex-row-reverse' : 'flex-row')}>
      <div className="h-7 w-7 rounded-full bg-zinc-200 dark:bg-zinc-700 shrink-0" />
      <div className="space-y-1.5 w-2/3">
        <div className={cn('flex gap-2', isAdmin ? 'flex-row-reverse' : '')}>
          <div className="h-2.5 bg-zinc-200 dark:bg-zinc-700 rounded w-20" />
          <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded w-16" />
        </div>
        <div className="rounded-2xl px-3.5 py-3 bg-zinc-100 dark:bg-zinc-800 space-y-1.5">
          <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-full" />
          <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4" />
        </div>
      </div>
    </div>
  );
}
